import { Button, Input, Notify } from '@airdao/ui-library';
import React, { useEffect, useMemo, useState } from 'react';
import ambIcon from '../../../assets/icons/amb.svg';
import useBondPeripheryContracts from '../../../contexts/BondPeripheryContext/useBondPeriphery';
import useBondContracts from '../../../contexts/BondContractContext/useBondContracts';
import { ContractNames as PeripheryNames } from '@airdao/bond-periphery';

import { MarketContextType, MarketType } from '../../../contexts/MarketsContext/provider.interface';
import { BigNumber, utils } from 'ethers';
import { calculatedWillGet, calculateMax, handleAmount, willGet } from '../exchange.service';
import { getTokenByAddress } from '../../../utils/getTokenByAddress';
import useMarkets from '../../../contexts/MarketsContext/useMarkets';
import { FPDA, SDA } from '../../../utils/constants';
import { formatDecimals } from '../../../utils/formatDecimals';
import { useParams } from 'react-router-dom';
// @ts-ignore
import { ContractNames } from '@airdao/airdao-bond';
import useGetMyRewards from '../../../hooks/useGetMyRewards';
import useGetEngagementByAddress from '../../../hooks/useGetEngagementByAddress';
import { useAccount } from 'wagmi';

const SpendRewards = ({marketData, protocolFee}: {marketData: MarketType, protocolFee: number}) => {
  const { id } = useParams();
  const { address: account } = useAccount();
  const { tokens }: MarketContextType = useMarkets();
  const {totalReward: bnBalance }:any = useGetMyRewards();
  const contracts = useBondContracts();
  const peripheryContracts: any = useBondPeripheryContracts();
  const { kosDiscount } = useGetEngagementByAddress();

  const stateRewards = useGetMyRewards();

  const [amount, setAmount] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    setError('');
  }, [amount]);

  const tokensData = useMemo(() => {
    return {
      payout: getTokenByAddress(marketData.payoutToken, tokens),
      quote: getTokenByAddress(marketData.quoteToken, tokens),
    };
  }, [tokens]);

  const willGetSubFee = useMemo(() =>
      calculatedWillGet(marketData, +amount, (+protocolFee - (+protocolFee * +stateRewards.getDiscount / 100))),
    [marketData, amount, protocolFee]
  );

  const handleValue = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleAmount(e, setAmount, tokensData.quote?.contractAddress || '', tokens)
  };

  const formattedWillGetSubFee = +utils.formatUnits(
    willGetSubFee,
    tokensData.quote?.decimals,
  );

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    if (willGetSubFee.gte(BigNumber.from(marketData.maxPayout))) {
      setError('Enter amount less then max bondable value');
      return;
    } else if (utils.parseUnits(amount).gt(bnBalance)) {
      setError('Insufficient balance');
      return;
    } else if (willGet(amount, marketData.bondMarketPrice).gt(BigNumber.from(marketData.capacity))) {
      setError('Enter amount less then available amount');
      return;
    }
    if (tokensData && tokensData.quote) {
      Notify.loading('Transaction in progress.', null, {
        toastId: 'purchase-loading',
      });
      setIsLoading(true);
      const bmPurchaser = peripheryContracts.getContractByName(PeripheryNames.UserBMPurchaser);
      const bondAggregator = contracts.getContractByName(ContractNames.Aggregator);

      try {
        await (
          await bmPurchaser.purchaseForMe(
            bondAggregator.address,
            account,
            id,
            utils
              .parseUnits(amount, tokensData.quote.decimals)
              .toString(),
            marketData.marketType === SDA ? willGetSubFee : '0',
            true
          )
        )
          .wait()
          .then(() => {
            Notify.success(
              `Success! You bought ${formatDecimals(
                formattedWillGetSubFee,
                tokensData.payout?.contractAddress,
                tokens,
              )} ${tokensData.payout?.symbol.toUpperCase()}`,
              null,
              { autoClose: 5000 },
            );
            setAmount('0');
          });
      } catch (e: any) {
        console.log(e, e.code);
        if (e.code !== 'ACTION_REJECTED') {
          Notify.error('Error', null, {});
        }
      }
      setAmount('');
      setIsLoading(false);
      Notify.dismiss('purchase-loading');
    }
  };
  const marketNotStarted = marketData.start > new Date().getTime() / 1000;
  const setMax = () => {
    let newAmount = +calculateMax(marketData, bnBalance);
    if (marketData.marketType === FPDA) {
      newAmount = +newAmount - (+newAmount * +utils.formatEther(kosDiscount));
    }
    setAmount(
      formatDecimals(
        newAmount,
        tokensData.quote?.contractAddress,
        tokens,
      ).toString(),
    );
  };

  if (+utils.formatEther(bnBalance) < 1000 || !account) {
    return null;
  }

  return (
    <form className="spend-rewards" onSubmit={handleSubmit}>
      <p className="spend-rewards__label">Spend Your Rewards</p>
      <Input
        onChange={handleValue}
        placeholder="0"
        value={amount}
        className="exchange-input spend-rewards__input"
        leadIcon={
          <span className="exchange-input__lead">
            <img src={ambIcon} alt="ambrosus" />
            AMB
          </span>
        }
      />
      <div className="exchange-form__error">
        {error && (
          <span className="error-message error-message_rewards">{error}</span>
        )}
      </div>
      <p className="exchange-form__max">
        My Rewards Balance: {formatDecimals(utils.formatEther(bnBalance), tokensData.quote?.contractAddress, tokens)} AMB{" "}
        <button type="button" onClick={setMax}>Max</button>
      </p>
      <div className="exchange-form__will-get">
        <p>You will get</p>
        <p>{formatDecimals(formattedWillGetSubFee, tokensData.payout?.contractAddress, tokens)} {tokensData.payout?.symbol.toUpperCase()}</p>
      </div>
      <Button
        type="primary"
        size="large"
        disabled={
          !+amount || !marketData.isLive || isLoading || marketNotStarted
        }
        className="exchange-form__submit"
        onClick={handleSubmit}
      >
        {!amount ? 'Enter amount to purchase' : marketData.isLive ? 'Buy With Rewards' : 'Market closed'}
      </Button>
    </form>
  )
};

export default SpendRewards;
